import { formatAppTime } from "./appTimeFormat";

export const formatViewerCount = (n) => {
  const count = Number(n) || 0;
  return `${count.toLocaleString()} view${count === 1 ? "" : "s"}`;
};

export const viewerViewedDateKey = (row) => {
  if (!row?.viewedAt) return "unknown";
  const d = new Date(row.viewedAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatViewTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppTime(d);
};

export const formatPeakHour = (hour) => {
  if (hour == null || Number.isNaN(Number(hour))) return "—";
  return `${String(hour).padStart(2, "0")}:00`;
};

/** Pulse from GET /api/bet/:houseId/viewers — aggregated from bet.viewers in MongoDB. */
export const normalizeViewerPulse = (pulse) => {
  if (!pulse || typeof pulse !== "object") {
    return { loaded: false };
  }

  return {
    loaded: true,
    totalViews: Number(pulse.totalViews) || 0,
    uniqueViewers: pulse.uniqueViewers ?? null,
    repeatViewers: Number(pulse.repeatViewers) || 0,
    latestViewedAt: pulse.latestViewedAt ?? null,
    peakHour: pulse.peakHour ?? null,
    peakHourCount: Number(pulse.peakHourCount) || 0,
    byHour: Array.isArray(pulse.byHour) ? pulse.byHour.map((n) => Number(n) || 0) : null,
    dailyTrend: Array.isArray(pulse.dailyTrend) ? pulse.dailyTrend : null,
    topViewers: Array.isArray(pulse.topViewers) ? pulse.topViewers : null,
  };
};

export const sumSeries = (values = []) =>
  (values || []).reduce((sum, value) => sum + (Number(value) || 0), 0);

export const maxSeriesValue = (values) =>
  Math.max(0, ...(values || []).map((v) => Number(v) || 0));

export const compactNumber = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

/**
 * Stickiness math from the viewer pulse: how much attention each viewer
 * gives (views per unique) and how many come back (repeat share).
 */
export const computeStickiness = (pulse) => {
  const totalViews = Number(pulse?.totalViews) || 0;
  const unique = Number(pulse?.uniqueViewers) || 0;
  const repeat = Number(pulse?.repeatViewers) || 0;
  const ratio = unique > 0 ? totalViews / unique : null;
  const repeatShare = unique > 0 ? repeat / unique : null;
  return {
    totalViews,
    uniqueViewers: unique,
    repeatViewers: repeat,
    ratio,
    ratioLabel: ratio != null ? `${(Math.round(ratio * 10) / 10).toFixed(1)}` : null,
    repeatShare,
    repeatPct: repeatShare != null ? Math.round(repeatShare * 100) : null,
    latestViewedAt: pulse?.latestViewedAt ?? null,
  };
};

/**
 * Attention-to-action funnel from the operator dashboard:
 * viewers → followers → unique bettors, with stage-to-stage conversion.
 */
export const buildConversionStages = (dashboard) => {
  const company = dashboard?.company || {};
  const customers = dashboard?.customers || {};
  const activity = dashboard?.activity || {};

  const viewers = Number(company.viewers) || 0;
  const followers = Number(company.followers) || 0;
  const bettors = Number(customers.totalUnique) || 0;

  const pct = (num, den) => (den > 0 ? Math.round((num / den) * 100) : null);

  return {
    viewers,
    followers,
    bettors,
    totalViews: Number(company.totalViews) || 0,
    totalTickets: Number(activity.totalTickets) || 0,
    followRate: pct(followers, viewers),
    betRate: pct(bettors, followers),
    overallRate: pct(bettors, viewers),
    hasAny: viewers > 0 || followers > 0 || bettors > 0,
  };
};

/**
 * Window totals + day-over-day delta + best day for the 7-day trend.
 * Expects normalized daily items [{ key, label, value }].
 */
export const computeTrendCallout = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return { total: 0, delta: null, bestIndex: null, best: null };
  let total = 0;
  let bestIndex = null;
  for (let i = 0; i < list.length; i += 1) {
    const v = Number(list[i].value) || 0;
    total += v;
    if (v > 0 && (bestIndex == null || v > (Number(list[bestIndex].value) || 0))) {
      bestIndex = i;
    }
  }
  const last = Number(list[list.length - 1]?.value) || 0;
  const prev = list.length > 1 ? Number(list[list.length - 2]?.value) || 0 : null;
  return {
    total,
    delta: prev != null ? last - prev : null,
    bestIndex,
    best: bestIndex != null ? list[bestIndex] : null,
  };
};

export const normalizeViewerRow = (row) => ({
  userId: row.userId,
  username: row.username || "Viewer",
  viewedAt: row.viewedAt,
});

export const normalizeViewerList = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(normalizeViewerRow);
